import { Box } from '@mui/material';
import React from 'react';
import { TableType } from '../../../models/ContentManagementInterface';
import type BackendService from '../../../services/admin/BackendService';
import BlockFootnotesEditor from './BlockFootnotesEditor';
import InlineSectionHeader from './InlineSectionHeader';
import PasteReplaceDialog from './PasteReplaceDialog';
import { createEmptyTable } from './tableModel';
import TableEditorViewTabs from './TableEditorViewTabs';
import TablePlaceholder from './TablePlaceholder';
import { useTableBlockEditorState } from './useTableBlockEditorState';

interface TableBlockEditorProps {
  block: TableType;
  onChange: (props: TableType['properties']) => void;
  modelId: string;
  modelType?: string;
  fundOpportunityId?: string;
  backendService?: BackendService;
  /** Shows the Footnotes panel — toggled from a button in this block's toolbar strip. */
  footnotesOpen?: boolean;
}

function TableBlockContent({
  properties,
  editor,
  viewTab,
  setViewTab,
  importer,
  mobileView,
  onChange,
}: {
  properties: TableType['properties'];
  editor: ReturnType<typeof useTableBlockEditorState>['editor'];
  viewTab: ReturnType<typeof useTableBlockEditorState>['viewTab'];
  setViewTab: ReturnType<typeof useTableBlockEditorState>['setViewTab'];
  importer: ReturnType<typeof useTableBlockEditorState>['importer'];
  mobileView: ReturnType<typeof useTableBlockEditorState>['mobileView'];
  onChange: (props: TableType['properties']) => void;
}) {
  if (properties.rows.length === 0) {
    return (
      <TablePlaceholder
        onCreate={() => onChange({ ...properties, rows: createEmptyTable(3, 3) })}
      />
    );
  }
  return (
    <TableEditorViewTabs
      viewTab={viewTab}
      onViewTabChange={setViewTab}
      editor={editor}
      importer={importer}
      mobileView={mobileView}
      rows={properties.rows}
    />
  );
}

/**
 * Model-driven Table editor. Structure changes go through `tableModel` ops that
 * re-normalise the grid, so the block only ever holds what `TableContent` allows.
 */
export default function TableBlockEditor({
  block,
  onChange,
  modelId,
  modelType = 'FundOpportunity',
  backendService,
  footnotesOpen = false,
}: TableBlockEditorProps) {
  const state = useTableBlockEditorState(block, onChange, modelId, backendService, modelType);
  const { properties, pendingRows, setPendingRows, importer } = state;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <InlineSectionHeader
        heading={properties.heading}
        onChange={(heading) => onChange({ ...properties, heading })}
      />
      <TableBlockContent
        properties={properties}
        editor={state.editor}
        viewTab={state.viewTab}
        setViewTab={state.setViewTab}
        importer={importer}
        mobileView={state.mobileView}
        onChange={onChange}
      />
      {footnotesOpen && (
        <BlockFootnotesEditor
          footnotes={properties.footnotes}
          onChange={(footnotes) => onChange({ ...properties, footnotes })}
        />
      )}
      <PasteReplaceDialog
        open={pendingRows !== null}
        onConfirm={() => {
          if (pendingRows) onChange({ ...properties, rows: pendingRows });
          setPendingRows(null);
        }}
        onCancel={() => setPendingRows(null)}
      />
    </Box>
  );
}
